export type SessionDataEntry = [name: string, value: unknown]

function childEntries(value: unknown): SessionDataEntry[] | null {
  if (Array.isArray(value)) {
    return value.map((item, index) => [`[${index}]`, item])
  }
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
  }
  return null
}

function DataLeaf({ value }: { value: unknown }) {
  if (typeof value === 'string') {
    return (
      <span className="text-xs text-gray-900 break-all whitespace-pre-wrap">
        {value}
      </span>
    )
  }

  return (
    <span
      className={
        value == null ? 'text-xs text-gray-400 italic' : 'text-xs text-gray-900'
      }>
      {value == null ? '—' : String(value)}
    </span>
  )
}

function DataRow({ name, value }: { name: string; value: unknown }) {
  const children = childEntries(value)

  if (children === null || children.length === 0) {
    return (
      <li className="flex items-start gap-2">
        <span className="shrink-0 text-xs text-gray-500">{name}:</span>
        {children === null ? (
          <DataLeaf value={value} />
        ) : (
          <span className="text-xs text-gray-400 italic font-mono">
            {Array.isArray(value) ? '[]' : '{}'}
          </span>
        )}
      </li>
    )
  }

  return (
    <li>
      <span className="text-xs text-gray-500">{name}:</span>
      <SessionDataTree entries={children} nested />
    </li>
  )
}

/** Shared nested key/value rendering for session facts, extracts, and metadata. */
export function SessionDataTree({
  entries,
  nested = false,
}: {
  entries: SessionDataEntry[]
  nested?: boolean
}) {
  return (
    <ul
      className={
        nested
          ? 'mt-1 ml-2 space-y-1 border-l border-gray-200 pl-3'
          : 'space-y-1'
      }>
      {entries.map(([name, value]) => (
        <DataRow key={name} name={name} value={value} />
      ))}
    </ul>
  )
}
